import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useAuthMe } from '../hooks/useAuth';
import { ImpersonationBanner } from './ImpersonationBanner';

export function AuthGuard() {
  const { t } = useTranslation('common');
  const accessToken = useAuthStore((s) => s.accessToken);
  const isLoading = useAuthStore((s) => s.isLoading);
  const hydrateFromStorage = useAuthStore((s) => s.hydrateFromStorage);
  const location = useLocation();

  // Hydrate auth from localStorage on mount
  useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  // Validate token against /auth/me
  const { isLoading: isMeLoading, isOnboarded } = useAuthMe();

  // Show loading spinner while checking auth
  if (isLoading || isMeLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <p className="text-sm text-surface-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // No token → redirect to login
  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Not onboarded → redirect to onboarding (unless already there)
  if (!isOnboarded && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Already onboarded → redirect away from onboarding
  if (isOnboarded && location.pathname === '/onboarding') {
    return <Navigate to="/" replace />;
  }

  // M2 fix (whole-branch review): mounted here, above BOTH children of this
  // guard — /onboarding and DashboardLayout — instead of inside
  // DashboardLayout alone. An impersonated admin landing on an org that
  // hasn't onboarded used to reach a bare form with no banner and no exit.
  //
  // This div, not a bare fragment, is what makes that actually work: neither
  // html/body/#root (see packages/ui/src/global.css, index.html) nor this
  // component previously gave the banner+content pair an explicit height, so
  // the banner and a full-height page stacked to banner-height + 100vh — a
  // body scrollbar appeared, and the banner (and its Exit control) scrolled
  // away with the rest of the page. AuthGuard now owns the single h-screen
  // box; DashboardLayout and OnboardingPage are flex children that fill
  // whatever height is left after the banner (`flex-1 min-h-0`), each
  // managing its own internal scrolling exactly as before.
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-50">
      <ImpersonationBanner />
      <Outlet />
    </div>
  );
}
